import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { Badge } from '@/components/ui/badge';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { VENUE_EVENTS, rooms, type Branch, type MenuChannel } from '@/lib/shared-types';
import { fromDatetimeLocal } from './lib';
import { useBranchAssortmentQuery, type BranchAssortmentItem } from './menuGovernanceApi';

const CHANNELS: MenuChannel[] = ['DINE_IN', 'TAKEAWAY', 'DELIVERY', 'ONLINE'];
const CHANNEL_KEY: Record<MenuChannel, string> = {
  DINE_IN: 'menuGovernance.editor.channelDineIn',
  TAKEAWAY: 'menuGovernance.editor.channelTakeaway',
  DELIVERY: 'menuGovernance.editor.channelDelivery',
  ONLINE: 'menuGovernance.editor.channelOnline',
};

/**
 * Screen 6 — `asOf` history. "What were we charging at 13:20 on Friday", with
 * no backup: `GET /menu-governance/branches/:id/assortment?asOf=` resolves
 * latest-wins-as-of over every live rule, so a past moment, right now, or a
 * moment a scheduled publish has not reached yet are all the same read.
 *
 * ## What this screen honestly shows, and what it does not
 *
 * The endpoint answers with the rules that have ever been GOVERNED for this
 * branch — it is `branchMenuRule.findMany` resolved down to winners, not the
 * branch's full sellable assortment. An item nobody has ever published a rule
 * for is invisible here, not because it is withdrawn, but because governance
 * has never spoken about it: it is still on sale at `CatalogItem.basePrice`,
 * exactly as it always was (ADR-0011 §5.3, §5.4 — this layer is inert until
 * someone publishes). The empty state below says that in words instead of
 * reading as "this branch sells nothing".
 *
 * ## Empty vs broken
 *
 * `assertBranchReadable` (`branch-menu-resolver.ts`) exists precisely because
 * the runtime role is NOBYPASSRLS and an unbound read returns **zero rows**
 * rather than erroring — "no rule ever applied" and "the request never really
 * ran" would otherwise be indistinguishable. `branchAssortment` calls it
 * FIRST and unconditionally, so a broken read reaches this screen as a
 * thrown 500, not as an empty `items: []`. RTK Query's `isError` is what
 * renders the *broken* state below; an empty `items` array with no error
 * renders the *inert* state instead — the two must never look the same.
 */
export default function BranchHistoryPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const canReadBranches = can('branches.view');
  const branchesQuery = useApi<unknown>(canReadBranches ? '/branches' : null, { limit: 200 });
  const branches = useMemo(() => rowsOf<Branch>(branchesQuery.data).rows, [branchesQuery.data]);

  const [branchId, setBranchId] = useState('');
  const [asOfLocal, setAsOfLocal] = useState('');
  const [channel, setChannel] = useState<string>('');

  const asOf = fromDatetimeLocal(asOfLocal);
  const { data, isFetching, isError, refetch } = useBranchAssortmentQuery(
    { branchId, asOf: asOf ?? undefined, channel: (channel || undefined) as MenuChannel | undefined },
    { skip: !branchId },
  );

  /**
   * A publish or rollback landing on the chosen branch can move what "now"
   * resolves to, so this refetches on any of the six events for THAT branch's
   * room — not the header's active branch, which the operator may have picked
   * differently from the one they are asking this screen's history about.
   * Refetching a fixed past `asOf` on an unrelated live event is a wasted
   * request rather than a wrong answer: `resolveEffectiveRules` is pure over
   * `effectiveFrom <= asOf`, so nothing before "now" ever changes.
   */
  const branchRoom = branchId ? [rooms.branch(branchId)] : [];
  useRealtime(VENUE_EVENTS.MENU_VERSION_SUBMITTED, refetch, branchRoom);
  useRealtime(VENUE_EVENTS.MENU_VERSION_APPROVED, refetch, branchRoom);
  useRealtime(VENUE_EVENTS.MENU_VERSION_REJECTED, refetch, branchRoom);
  useRealtime(VENUE_EVENTS.MENU_VERSION_SCHEDULED, refetch, branchRoom);
  useRealtime(VENUE_EVENTS.MENU_VERSION_PUBLISHED, refetch, branchRoom);
  useRealtime(VENUE_EVENTS.MENU_VERSION_ROLLED_BACK, refetch, branchRoom);
  useRealtimeResync(refetch);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('menuGovernance.history.branch')} controlId="mg-hist-branch">
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="w-56">
            <option value="">{t('menuGovernance.history.branchPlaceholder')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('menuGovernance.history.asOf')} hint={t('menuGovernance.history.asOfNow')} controlId="mg-hist-asof">
          <Input
            type="datetime-local"
            value={asOfLocal}
            onChange={(e) => setAsOfLocal(e.target.value)}
            className="w-56"
          />
        </Field>
        <Field label={t('menuGovernance.history.channel')} controlId="mg-hist-channel">
          <Select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-44">
            <option value="">{t('menuGovernance.history.allChannels')}</option>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t(CHANNEL_KEY[c])}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {!branchId ? (
        <EmptyState
          title={t('menuGovernance.history.chooseBranchTitle')}
          hint={t('menuGovernance.history.chooseBranchHint')}
        />
      ) : isFetching && !data ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState
          message={`${t('menuGovernance.history.errorTitle')} — ${t('menuGovernance.history.errorHint')}`}
          onRetry={() => refetch()}
        />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title={t('menuGovernance.history.emptyTitle')} hint={t('menuGovernance.history.emptyHint')} />
      ) : (
        <AssortmentTable branchId={branchId} items={data.items} language={i18n.language} t={t} />
      )}
    </div>
  );
}

function AssortmentTable({
  branchId,
  items,
  t,
}: {
  branchId: string;
  items: BranchAssortmentItem[];
  language: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const { format } = useCurrency(branchId);

  return (
    <TableWrap>
      <Table>
        <THead>
          <TR>
            <TH>{t('menuGovernance.history.columnItem')}</TH>
            <TH>{t('menuGovernance.history.columnCarried')}</TH>
            <TH>{t('menuGovernance.history.columnPrice')}</TH>
            <TH>{t('menuGovernance.history.columnChannels')}</TH>
            <TH>{t('menuGovernance.history.columnVersion')}</TH>
          </TR>
        </THead>
        <tbody>
          {items.map((item) => (
            <TR key={item.itemId}>
              <TD className="font-medium">{item.name}</TD>
              <TD>
                <Badge tone={item.carried ? 'success' : 'danger'}>
                  {t(item.carried ? 'menuGovernance.common.carried' : 'menuGovernance.common.notCarried')}
                </Badge>
              </TD>
              <TD className="tabular-nums">
                {!item.carried ? (
                  '—'
                ) : item.price === null ? (
                  <span className="text-ink-muted">
                    {t('menuGovernance.editor.inherits', { price: item.catalogPrice ? format(item.catalogPrice) : '—' })}
                  </span>
                ) : (
                  format(item.price)
                )}
              </TD>
              <TD>
                {item.channels.length === 0
                  ? t('menuGovernance.history.allChannels')
                  : item.channels.map((c) => t(CHANNEL_KEY[c])).join(', ')}
              </TD>
              <TD className="tabular-nums text-ink-muted">#{item.versionNo}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}
